/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import ConfirmModal from "./ConfirmModal";
import ModalPersona from "../personas/ModalPersonas";
import "../../styles/AdminEntities.css"; 
import AlertModal from "../common/AlertModal";
import { useMsal } from "@azure/msal-react"
import useVersionMovil from "../../hooks/useMobileVersion";

/**
 * Componente de administración de personal.
 * Permite listar, filtrar, crear, editar y eliminar personas,
 * gestionando autenticación mediante Microsoft Entra (MSAL).
 */
const AdminPersonas = () => {

  /* ============================
     Estado principal de datos
     ============================ */
  const isMobile = useVersionMovil();
  
  // Lista de personas obtenidas desde la API
  const [personas, setPersonas] = useState([]);
  
  // Lista de departamentos disponibles para asignar a una persona
  const [departamentos, setDepartamentos] = useState([]);

  // Lista de edificios disponibles para asignar a una persona
  const [edificios, setEdificios] = useState([]);

  /* ============================
     Estado de modales y acciones
     ============================ */

  // Controla la apertura del modal de confirmación de borrado
  const [modalOpen, setModalOpen] = useState(false);

  // Persona seleccionada para eliminar
  const [personaToDelete, setPersonaToDelete] = useState(null);

  // Controla la apertura del modal de creación/edición
  const [modalFormOpen, setModalFormOpen] = useState(false);

  // Persona que se está editando (null si es creación)
  const [personaEdit, setPersonaEdit] = useState(null);

  /* ============================
          Estado de alertas
     ============================ */

  // Controla la visibilidad del modal de alertas
  const [alertModalOpen, setAlertModalOpen] = useState(false);

  // Mensaje mostrado en la alerta
  const [alertMessage, setAlertMessage] = useState("");

  // Tipo de alerta: success | error
  const [alertType, setAlertType] = useState("success");

  // Tipo de acción realizada (create o edit)
  const [actionType, setActionType] = useState("");

  /* ============================
     Estado de filtros de búsqueda
     ============================ */
  
  // Filtros aplicados a la búsqueda de trabajadores
  const [filtros, setFiltros] = useState({
    nombre: "",
    apellido1: "",
    apellido2: "",
    cargo: "",
    email: "",
    telefono: "",
    extension: "",
    departamento: "",
    edificio: "",
  });

  // Instancia de MSAL para gestión de autenticación
  const { instance, accounts } = useMsal();

  /**
    * Obtiene de forma silenciosa el access token de Microsoft Entra.
    * Es necesario para autenticar las peticiones a la API.
    */
  const getAccessToken = async () => {
    if (!accounts || accounts.length === 0) return null;
    try {
      const response = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [process.env.REACT_APP_API_SCOPE],
      });
      return response.accessToken;
    } catch (error) {
      console.error("Error obteniendo access token:", error);
      return null;
    }
  };

  /* ============================
     Fetch de datos desde la API
     ============================ */

  /**
   * Obtiene la lista de trabajadores desde la API,
   * aplicando los filtros actuales si existen.
   */
  const fetchPersonas = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const params = { ...filtros };

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/personas`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      const data = Array.isArray(res.data) ? res.data : res.data.content || [];
	 
      const dataOrdenada = data.sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
      );

      setPersonas(data);
    } catch (err) {
      console.error("Error fetching personas:", err);
      setAlertMessage("Error cargando personas desde la API");
      setAlertType("error");
      setAlertModalOpen(true);
    }
  };

  /**
   * Obtiene la lista de departamentos desde la API.
   * Se utiliza para el formulario de creación/edición.
   */
  const fetchDepartamentos = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/departamentos`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = Array.isArray(res.data) ? res.data : res.data.content || [];

      const dataOrdenada = data.sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
      );

      setDepartamentos(data);
    } catch (err) {
      console.error("Error fetching departamentos:", err);
      setAlertMessage("Error cargando departamentos desde la API");
      setAlertType("error");
      setAlertModalOpen(true);
    }
  };

  /**
   * Obtiene la lista de edificios desde la API.
   * Se utiliza para el formulario de creación/edición.
   */
  const fetchEdificios = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/edificios`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = Array.isArray(res.data) ? res.data : res.data.content || [];
      
      const dataOrdenada = data.sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
      );

      setEdificios(data);
    } catch (err) {
      console.error("Error fetching Edificios:", err);
      setAlertMessage("Error cargando edificios desde la API");
      setAlertType("error");
      setAlertModalOpen(true);
    }
  };

  /* ============================
              Effects
     ============================ */

  /**
   * Carga inicial de datos al montar el componente.
   */
  useEffect(() => {
    fetchPersonas();
    fetchDepartamentos();
    fetchEdificios();
  }, []);

  /**
   * Si se limpian todos los filtros, se recarga automáticamente
   * la lista completa de trabajadores.
   */
  useEffect(() => {
    if (
      filtros.nombre === "" &&
      filtros.apellido1 === "" &&
      filtros.apellido2 === "" &&
      filtros.cargo === "" &&
      filtros.email === "" &&
      filtros.telefono === "" &&
      filtros.extension === "" &&
      filtros.departamento === "" &&
      filtros.edificio === ""
    ) {
      fetchPersonas();
    }
  }, [filtros]);

  /* ============================
          Manejo de filtros
     ============================ */

  /**
   * Actualiza el estado de los filtros conforme el usuario escribe.
   */
  const handleFiltroChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };
  

  const handleFiltroChangeNum = (name, value) => {
    setFiltros((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Ejecuta la búsqueda aplicando los filtros actuales.
   */
  const handleBuscar = (e) => {
    e.preventDefault();
    fetchPersonas();
  };

  /**
   * Resetea todos los filtros y muestra todas las personas.
   */
  const handleReset = () => {
    setFiltros({
      nombre: "",
      apellido1: "",
      apellido2: "",
      cargo: "",
      email: "",
      telefono: "",
      extension: "",
      departamento: "",
      edificio: "",
    });
    fetchPersonas();
  };

  /* ============================
     Manejo de modales y acciones
     ============================ */

  /**
   * Abre el formulario en modo creación.
   */
  const handleCrear = () => {
    setPersonaEdit(null);
    setModalFormOpen(true);
    setActionType("create");
  };

  /**
   * Abre el formulario en modo edición con los datos de la persona.
   */
  const handleEditar = (p) => {
    setPersonaEdit(p);
    setModalFormOpen(true);
    setActionType("edit");
  };

  /**
   * Se ejecuta tras guardar correctamente una persona.
   * Refresca la tabla y muestra un mensaje de confirmación.
   */
  const handleGuardar = () => {
    fetchPersonas();
    setModalFormOpen(false);
    setAlertMessage(actionType === "create" ? "Trabajador creado correctamente" : "Cambios guardados");
    setAlertType("success");
    setAlertModalOpen(true);
  };

  /**
   * Abre el modal de confirmación de borrado.
   */
  const handleDeleteClick = (p) => {
    setPersonaToDelete(p);
    setModalOpen(true);
  };

  /**
   * Elimina definitivamente la persona seleccionada.
   */
  const handleConfirmDelete = async () => {
    if (!personaToDelete) return;

    try {
      const token = await getAccessToken();
      if (!token) return;

      await axios.delete(`${process.env.REACT_APP_API_URL}/personas/${personaToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchPersonas();
      setAlertMessage(`Trabajador "${personaToDelete.nombre} ${personaToDelete.apellido1}" eliminado correctamente`);
      setAlertType("success");
      setAlertModalOpen(true);
    } catch (err) {
      console.error(err);
      setAlertMessage("Hubo un error al eliminar el trabajador.");
      setAlertType("error");
      setAlertModalOpen(true);
    } finally {
      setModalOpen(false);
      setPersonaToDelete(null);
    }
  };

  /*****************************************************************
   * Renderizado de la interfaz de administración de personal.*
   *****************************************************************/
  return (
    /* Contenedor principal de la vista de administración */
    <div className="admin-personas-container">
      {/* Título de la sección */}
      <div className="text-center fw-bold fs-4">Gestión de Personal</div>
      
      {/* =============================
          Bloque de búsqueda y filtros
          ============================= */}
      <div className="buscador-card p-3 mb-3 me-2">
      <form onSubmit={handleBuscar}>
        
        {/* Campos de filtrado */}
        <div className={`filtros-grid ${isMobile ? 'filtros-mobile' : ''} mb-3`}>
          <input
            type="text"
            name="nombre"
            placeholder="Nombre"
            value={filtros.nombre}
            onChange={handleFiltroChange}
            maxLength={50}
          />
          <input
            type="text"
            name="apellido1"
            placeholder="Primer apellido"
            value={filtros.apellido1}
            onChange={handleFiltroChange}
            maxLength={50}
          />
          <input
            type="text"
            name="apellido2"
            placeholder="Segundo apellido"
            value={filtros.apellido2}
            onChange={handleFiltroChange}
            maxLength={50}
          />
          <input
            type="text"
            name="cargo"
            placeholder="Cargo"
            value={filtros.cargo}
            onChange={handleFiltroChange}
            maxLength={50}
          />
          <input
            type="text"
            name="email"
            placeholder="Email"
            value={filtros.email}
            onChange={handleFiltroChange}
            maxLength={50}
          />
          <input
            type="text"
            name="telefono"
            placeholder="Teléfono"
            value={filtros.telefono}
            maxLength={9}
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              handleFiltroChangeNum("telefono", value);
            }}
          />
          <input
            type="text"
            name="extension"
            placeholder="Extensión"
            value={filtros.extension}
            maxLength={4}
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              handleFiltroChangeNum("extension", value);
            }}
          />

          <select
            className="form-select"
            value={filtros.departamento}
            name="departamento"
            onChange={handleFiltroChange}
          >
            <option value="">Seleccione un departamento</option>
            {departamentos.map((d) => (
              <option key={d.id} value={d.nombre}>
                {d.nombre}
              </option>
            ))}
          </select>

          <select
            className="form-select"
            value={filtros.edificio}
            name="edificio"
            onChange={handleFiltroChange}
          >
            <option value="">Seleccione un edificio</option>
            {edificios.map((e) => (
              <option key={e.id} value={e.nombre}>
                {e.nombre}
              </option>
            ))}
          </select>

        </div>

        
        {/* Botonera de acciones del buscador */}
        <div className="botones-buscador">
          <button type="submit" className="btn-modern btn-modern-primary">
            Buscar
          </button>
          <button
            type="button"
            className="btn-modern btn-modern-secondary"
            onClick={handleReset}
          >
            Mostrar todo
          </button>

          {/* Abre el formulario para crear una nueva persona */}
          <button
            type="button"
            className="btn-modern btn-modern-create"
            onClick={handleCrear}
          >
            <i className="bi bi-plus-circle"></i> Añadir trabajador
          </button>
        </div>

      </form>
    </div>

      {/* =============================
              Tabla de resultados
          ============================= */}
      <div className="table-scroll-wrapper">
        {/* Contenedor con scroll horizontal */}
        <div className="table-scroll">
          <table className="table-modern">
            
            {/* Cabecera de la tabla */}
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Apellido1</th>
                <th>Apellido2</th>
                <th>Cargo</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Extensión</th>
                <th>Departamento</th>
                <th>Edificio</th>
                <th>Foto</th>
                <th>Acciones</th>
              </tr>
            </thead>
            {/* Cuerpo de la tabla con las personas obtenidas */}
            <tbody>
              {personas.map((p) => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{p.nombre}</td>
                  <td>{p.apellido1}</td>
                  <td>{p.apellido2}</td>
                  <td>{p.cargo ?? "-"}</td>
                  <td>{p.email}</td>
                  <td>{p.telefono ?? "-"}</td>
                  <td>{p.extension ?? "-"}</td>
                  <td>{p.departamento?.nombre || "-"}</td>
                  <td>{p.edificio?.nombre || "-"}</td>
                  <td className="foto-cell">
                    {p.fotoUrl ? (
                      <i className="bi bi-check-circle-fill text-success"></i>
                    ) : (
                      <i className="bi bi-x-circle-fill text-danger"></i>
                    )}
                  </td>
                  {/* Acciones disponibles para cada persona */}
                  <td className="actions-cell">
                    {/* Editar persona */}
                    <button className="btn-action btn-warning" onClick={() => handleEditar(p)}><i className="bi bi-pencil"></i></button>
                    {/* Eliminar persona */}
                    <button className="btn-action btn-danger" onClick={() => handleDeleteClick(p)}><i className="bi bi-trash"></i></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* =============================
            Modales de la aplicación
          ============================= */}
      {/* Modal de confirmación para eliminación de personas */}
      <ConfirmModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmDelete}
        message={`¿Eliminar a ${personaToDelete?.nombre} ${personaToDelete?.apellido1}?`}
      />
      {/* Modal de creación y edición de personas */}
      <ModalPersona
        show={modalFormOpen}
        onClose={() => setModalFormOpen(false)}
        onSave={handleGuardar}
        departamentos={departamentos}
        edificios={edificios}
        persona={personaEdit}
      />

      {/* Modal de alertas para feedback al usuario */}
      <AlertModal
        show={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        message={alertMessage}
        type={alertType}
      />
    </div>
  );
};

export default AdminPersonas;
